/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { get, memoize } from 'lodash/fp';
import styled from '@emotion/styled';
import t from 'app/utils/translate';
import CustomHelmet from 'app/components/commons/CustomHelmet';
import Button from 'app/components/commons/Button';
import InputField from 'app/components/commons/InputField';
import Page from 'app/containers/parts/Page';
import Responsive from 'app/components/responsive/Responsive';
import TextArea from 'app/components/commons/TextArea';
import Title from 'app/components/commons/Title';
import personalNameSelector from 'app/selectors/personalNameSelector';
import { submitEnquiry } from 'app/actions/contactActions';
import withNotificationCallback from 'app/components/notifications/withNotificationCallback';

type Props = {
  defaultEmailAddress?: string,
  defaultName?: string,
  enableAdditionalInfo?: boolean,
  errors: Array<Object>,
  isSignedIn: boolean,
  isSubmitting: boolean,
  lightContent: boolean,
  onSubmit: Object => void,
  transmitNotification: Object => void,
  htmlTitle?: string,
  description?: string,
  siteName: string
};

type State = {
  emailAddress: string,
  message: string,
  name: string
};

const Main = styled.div(({ theme }) => ({
  marginTop: 20,
  padding: theme.spacing.small
}));

const FormRow = styled.div(({ theme }) => ({
  margin: '0 -10px',
  [theme.mediaQuery.tabletLandscapeAndUp]: {
    display: 'flex'
  }
}));

const FormItem = styled.div(({ theme }) => ({
  padding: '0 10px',
  [theme.mediaQuery.tabletLandscapeAndUp]: {
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1
  }
}));

const Final = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  paddingTop: '1rem'
});

const StyledLink = styled.a(({ theme }) => ({
  color: theme.color.success
}));

class ContactUs extends React.Component<Props, State> {
  props: Props;

  state: State = {
    emailAddress: this.props.defaultEmailAddress || '',
    message: '',
    name: this.props.defaultName || ''
  };

  static defaultProps = {
    defaultEmailAddress: undefined,
    defaultName: undefined,
    enableAdditionalInfo: false,
    htmlTitle: undefined,
    description: undefined
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.isSignedIn !== this.props.isSignedIn) {
      this.setState({
        emailAddress: nextProps.defaultEmailAddress || '',
        name: nextProps.defaultName || ''
      });
    }
    if (this.props.isSubmitting && !nextProps.isSubmitting && nextProps.errors.length === 0) {
      this.setState({
        message: ''
      });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { errors, isSubmitting, transmitNotification } = this.props;

    if (!isSubmitting && prevProps.isSubmitting) {
      if (errors.length > 0) {
        transmitNotification({
          message: errors[0].message,
          type: 'danger'
        });
      } else {
        transmitNotification({
          message: 'Your enquiry was successfully submitted.',
          type: 'success'
        });
      }
    }
  }

  handleSubmit = () => {
    this.props.onSubmit({
      name: this.state.name,
      email: this.state.emailAddress,
      message: this.state.message
    });
  };

  setFormField = memoize((key: string) => (value: string) => {
    this.setState({
      [key]: value
    });
  });

  render() {
    const { enableAdditionalInfo, lightContent, htmlTitle, description, siteName } = this.props;
    const { emailAddress, isSubmitting, message, name } = this.state;

    return (
      <Page title={t('Contact Us')}>
        <CustomHelmet description={description} htmlTitle={htmlTitle} title={t('Contact Us')} />
        <Responsive mobile>
          <Title noWhiteSpace>{t('Contact Us')}</Title>
        </Responsive>
        <Main>
          {enableAdditionalInfo && (
            <div>
              <p>
                Please see the{' '}
                <StyledLink
                  href="https://playxchange.freshdesk.com/support/solutions"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  PlayXchange FAQs
                </StyledLink>{' '}
                prior to submitting a support ticket as there is likely to contain some information
                that will assist you in your problem.
              </p>
              <p>
                If you have a question or support issue regarding Two-Factor Authentication (2FA) or
                Know-Your-Customer (KYC) verification, please contact the{' '}
                <StyledLink
                  href="https://playxchange.freshdesk.com/support/home"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  PlayXchange Support Portal
                </StyledLink>{' '}
                directly. {siteName} Support cannot assist you with these queries.
              </p>
            </div>
          )}
          <form>
            <FormRow>
              <FormItem>
                <InputField
                  label={t('Name')}
                  type="text"
                  name="name"
                  value={name}
                  onChange={this.setFormField('name')}
                  required
                  lightContent={lightContent}
                />
              </FormItem>
              <FormItem>
                <InputField
                  label={t('Email Address')}
                  type="email"
                  name="emailAddress"
                  value={emailAddress}
                  onChange={this.setFormField('emailAddress')}
                  required
                  lightContent={lightContent}
                />
              </FormItem>
            </FormRow>
            <FormRow>
              <FormItem>
                <TextArea
                  label={t('Message')}
                  type="text"
                  name="message"
                  value={message}
                  onChange={this.setFormField('message')}
                  rows={5}
                  required
                  lightContent={lightContent}
                />
              </FormItem>
            </FormRow>
            <p>
              We will try to respond to your email within 4 hours during business hours (Monday to
              Sunday 6am - 12am AEST)
            </p>
            <Final>
              <Responsive tabletPortrait tabletLandsape desktop>
                {isLargeScreen => (
                  <Button
                    block={!isLargeScreen}
                    disabled={isSubmitting}
                    onClick={this.handleSubmit}
                  >
                    {t('Submit')}
                  </Button>
                )}
              </Responsive>
            </Final>
          </form>
        </Main>
      </Page>
    );
  }
}

const connector = connect(
  state => ({
    defaultEmailAddress: state.user.currentUser.email,
    defaultName: personalNameSelector(state),
    enableAdditionalInfo: get('enableAdditionalInfoOnContactUs', state.settings),
    errors: state.contact.errors,
    isSignedIn: state.user.isSignedIn,
    isSubmitting: state.contact.isSubmitting,
    lightContent: !state.settings.darkMode,
    htmlTitle: get('content.contactUs.htmlTitle', state.settings),
    description: get('content.contactUs.description', state.settings),
    siteName: get('siteName', state.settings)
  }),
  {
    onSubmit: submitEnquiry
  }
);

const enhancer = compose(
  withNotificationCallback,
  connector
);

export default enhancer(ContactUs);
